import type { ComponentProps } from 'react'
import { useIntl } from 'react-intl'
import FilterChip from 'LuxKit/Chips/FilterChip'
import TextButton from 'LuxKit/Button/TextButton'
import OfferSortControl from 'components/Common/OfferSortControl/OfferSortControl'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import useGlobalSearchURLHashFilter from 'hooks/GlobalSearch/useGlobalSearchURLHashFilter'
import useExperiment from 'hooks/Experiments/useExperiment'
import { Experiments } from 'constants/experiments'
import FilterDropdown from './FilterDropdown'
import { connect } from 'react-redux'
import getOfferListKey from 'lib/offer/offerListKey'
import { queryKeyAmenities, queryKeyHolidayTypes, queryKeyInclusions } from 'constants/url'
import CustomerRatingDropdown from './CustomerRatingDropdown'
import InclusionsDropdown from './InclusionsDropdown'
import Carousel from 'LuxKit/Carousel/Carousel'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import zIndex from 'styles/tools/z-index'
import { useInView } from 'react-intersection-observer'
import type { OfferListSortOption } from 'constants/offerListFilters'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { useScreenSizeOnly } from '../../../../hooks/useScreenSize'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'

const HeaderContainer = styled.div`
  position: sticky;
  z-index: ${zIndex.searchSubMenu};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  width: 100%;
  top: 0;

  &.lifted {
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.05);
  }
`

const ControlsRow = styled.div`
  display: flex;
  gap: ${rem(8)};

  > * {
    flex: 1;
    justify-content: center;
  }

  ${mediaQueryUp.tablet} {
    > * {
      flex: 0 0 auto;
    }
  }
`

interface MappedStateProps {
  availableFilters: App.OfferListFilterOptions | undefined
  search: string
}

type InheritedOfferSortControlProps = Pick<ComponentProps<typeof OfferSortControl>, 'onSortChange'>

interface Props extends InheritedOfferSortControlProps {
  filters: App.OfferListFilters
  showSort?: boolean
  sortOptions?: Array<OfferListSortOption>
  overrideAvailableFilters?: App.OfferListFilterOptions
  onFilterChange: FilterControlChangeHandler
  excludeOfferTypesFromCount?: boolean
  /** @deprecated Prefer `filterChipSize="large"`. */
  largeFilterChipSize?: boolean
  filterChipSize?: ComponentProps<typeof FilterChip>['size']
  mapViewUrl?: string
}

function HotelExposedFiltersHeader(props: Props & MappedStateProps) {
  const {
    availableFilters,
    overrideAvailableFilters,
    filters,
    search,
    showSort = false,
    sortOptions,
    onFilterChange,
    onSortChange,
    excludeOfferTypesFromCount = false,
    largeFilterChipSize = false,
    filterChipSize,
    mapViewUrl,
  } = props
  const intl = useIntl()

  const chipSize = filterChipSize ?? (largeFilterChipSize ? 'large' : 'medium')
  const filterOptions = overrideAvailableFilters ?? availableFilters
  const { toggleFilterPanel } = useGlobalSearchURLHashFilter()
  const isMobile = useScreenSizeOnly('mobile')
  const isMobileOrTablet = useIsMobileOrTablet()
  const { isAnyVariant: isSearchNavRedesign } = useExperiment(Experiments.searchMobileWebNavRedesign, { canActivate: false })
  const showSortDropdown = !isMobile

  const [toLiftRef, liftInView] = useInView()

  const offerTypesCount = excludeOfferTypesFromCount ? 0 : (filters.offerTypes?.length === 1 ? 1 : 0)
  const filterCount = (filters.holidayTypes?.length ?? 0) + (filters.amenities?.length ?? 0) + (filters.inclusions?.length ?? 0) +
    (filters?.priceLte ? 1 : 0) + (filters.locations?.length ?? 0) + (filters.customerRatingGte ? 1 : 0) + offerTypesCount

  const allFiltersText = intl.formatMessage({
    id: '1CpQrQpseRhWVRxZvWhW',
    defaultMessage: 'All filters',
    description: 'Form field label for button that opens all filters panel',
  })

  const customerRatingLabel = intl.formatMessage({
    id: 'Fmg8oNNFsuvKEX3qm8R6',
    defaultMessage: 'Customer rating',
    description: 'Label for customer rating filter dropdown in the filters panel',
  })

  const inclusionsLabel = intl.formatMessage({
    id: 'xqxLhsHQEb9XU42gCgr9',
    defaultMessage: 'Inclusions',
    description: 'Label for inclusions filter dropdown in the search or listing filters panel',
  })

  const typeOfEscapeLabel = intl.formatMessage({
    id: '6zL0mfztDFpmoqBdhxpA',
    defaultMessage: 'Type of escape',
    description: 'Label for holiday types filter dropdown in the search or listing filters panel',
  })

  const amenitiesLabel = intl.formatMessage({
    id: 'SDrddjVHD4KqX763IouH',
    defaultMessage: 'Amenities',
    description: 'Label for amenities filter dropdown in the search or listing filters panel',
  })

  const filterCountLabel = filterCount ? intl.formatMessage(
    {
      id: 'kP4ohcXYgX3_NhsaOJVg',
      defaultMessage: '({filterCount, number})',
      description: 'Active filter count in parentheses in the search or listing filters panel',
    },
    { filterCount },
  ) : ''

  const filterAndSortText = intl.formatMessage({
    id: 'Kwa_NLK1C0MSWf1ZnCg5',
    defaultMessage: 'Filter & Sort',
    description: 'Button label on mobile search results that opens the combined filters and sort panel',
  })

  const mapText = intl.formatMessage({
    id: 'dgZ0q4-mlU-8LQv1Cntf',
    defaultMessage: 'Map',
    description: 'Button label to switch to the map view display in the search results interface',
  })

  if (isSearchNavRedesign && isMobileOrTablet) {
    return (
      <>
        <div ref={toLiftRef} />
        <HeaderContainer className={!liftInView ? 'lifted' : ''}>
          <ControlsRow>
            <TextButton
              kind="secondary"
              variant="default"
              size="small"
              shape="round"
              startIcon={<LineFilterAltIcon />}
              onClick={toggleFilterPanel}
            >
              {filterAndSortText} {filterCountLabel}
            </TextButton>
            {mapViewUrl && (
              <TextButton
                kind="secondary"
                variant="default"
                size="small"
                shape="round"
                startIcon={<LineMapIcon />}
                to={mapViewUrl}
              >
                {mapText}
              </TextButton>
            )}
          </ControlsRow>
        </HeaderContainer>
      </>
    )
  }

  return (
    <>
      <div ref={toLiftRef} />
      <HeaderContainer className={!liftInView ? 'lifted' : ''}>
        <Carousel gap={8} gutterStyle="overflow">
          <FilterChip
            size={chipSize}
            startIcon={<LineFilterAltIcon />}
            onClick={toggleFilterPanel}
            selected={filterCount > 0}
          >
            {allFiltersText} {filterCountLabel}
          </FilterChip>
          {showSort && !!sortOptions?.length && (
            <OfferSortControl
              sortOptions={sortOptions}
              roundedButton
              showSelectedHighlight
              showDropdown={showSortDropdown}
              filterChipSize={chipSize}
              onSortChange={onSortChange}
            />
          )}
          <>
            <CustomerRatingDropdown filterChipSize={chipSize} filters={filters} search={search} label={customerRatingLabel} onFilterChange={onFilterChange} />
            <InclusionsDropdown
              filterChipSize={chipSize}
              searchQueryKey={queryKeyInclusions}
              label={inclusionsLabel}
              availableFilters={filterOptions?.filters?.inclusions}
              selectedFilters={filters?.inclusions ?? []}
              search={search}
              onFilterChange={onFilterChange}
            />
            <FilterDropdown
              filterChipSize={chipSize}
              searchQueryKey={queryKeyHolidayTypes}
              label={typeOfEscapeLabel}
              availableFilters={filterOptions?.filters?.holidayTypes}
              filterOrder={filterOptions?.filterOrder?.holidayTypes}
              selectedFilters={filters?.holidayTypes ?? []}
              search={search}
              onFilterChange={onFilterChange}
            />
            <FilterDropdown
              filterChipSize={chipSize}
              searchQueryKey={queryKeyAmenities}
              label={amenitiesLabel}
              availableFilters={filterOptions?.filters?.amenities}
              filterOrder={filterOptions?.filterOrder?.amenities}
              selectedFilters={filters?.amenities ?? []}
              search={search}
              onFilterChange={onFilterChange}
            />
          </>
        </Carousel>
      </HeaderContainer>
    </>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (appState, ownProps): MappedStateProps => {
    const key = getOfferListKey(ownProps.filters)
    const availableFilters = appState.offer.offerListFilterOptions
    return {
      availableFilters: availableFilters[key],
      search: appState.router.location.search,
    }
  },
)(HotelExposedFiltersHeader)
